import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Menu, X, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

const navLinks = [
  { label: 'Platform', href: '#platform' },
  { label: 'Features', href: '#features' },
  { label: 'Security', href: '#security' },
  { label: 'Pricing', href: '#pricing' },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'border-b border-dark-800/80 bg-dark-950/90 backdrop-blur-xl shadow-lg shadow-dark-950/50'
          : 'border-b border-transparent bg-transparent'
      )}
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <div className="flex h-18 items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <Shield className="h-8 w-8 text-cyber-400 transition-colors group-hover:text-cyber-300" />
              <div className="absolute inset-0 blur-lg bg-cyber-400/20 group-hover:bg-cyber-400/30 transition-colors" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight text-white">
                Identity<span className="text-cyber-400">Central</span>
              </span>
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-dark-500 -mt-0.5">
                Identity Intelligence
              </span>
            </div>
          </Link>

          {/* Desktop nav links */}
          {isLanding && (
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="px-4 py-2 text-[15px] font-medium text-dark-300 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/auth/signin"
              className="px-5 py-2.5 text-sm font-medium text-dark-200 hover:text-white transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/auth/waitlist"
              className="group flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-dark-950 bg-cyber-400 rounded-lg hover:bg-cyber-300 transition-all shadow-lg shadow-cyber-500/25 hover:shadow-cyber-500/40"
            >
              Join the Waitlist
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2.5 text-dark-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-dark-800/50 bg-dark-950/98 backdrop-blur-xl"
          >
            <div className="px-6 py-6 space-y-1">
              {isLanding &&
                navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-3 text-base font-medium text-dark-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
              <div className={cn('flex flex-col gap-3', isLanding && 'pt-4 mt-4 border-t border-dark-800')}>
                <Link
                  to="/auth/signin"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 text-base font-medium text-dark-200 hover:text-white transition-colors text-center rounded-lg hover:bg-white/5"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth/waitlist"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 text-base font-semibold text-dark-950 bg-cyber-400 rounded-lg text-center hover:bg-cyber-300 transition-colors"
                >
                  Join the Waitlist
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
